import { toLocalISODate } from '../utils/dateUtils';
import { confirmDialog, reportLoadError, notify } from '../utils/notify';
import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { exportToExcel } from '../utils/excelExport';
import { useLanguage } from '../contexts/LanguageContext';
import Modal from '../components/Modal';
import {
  Banknote, CircleCheck, ClipboardList, Coins, Download, Eye, Hourglass, Inbox, X,
} from 'lucide-react';
import StatusBadge from '../components/StatusBadge';
import './PayrollConsolidation.css';

const PayrollConsolidation = () => {
  const { t, locale } = useLanguage();
  const [activeTab, setActiveTab] = useState('consolidate');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Consolidate Tab State
  const [availablePayrolls, setAvailablePayrolls] = useState([]);
  const [selectedPayrolls, setSelectedPayrolls] = useState(new Set());
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    department: ''
  });

  // History Tab State
  const [consolidations, setConsolidations] = useState([]);
  const [selectedConsolidation, setSelectedConsolidation] = useState(null);
  const [consolidationItems, setConsolidationItems] = useState([]);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    payment_date: toLocalISODate(new Date()),
    payment_reference: '',
    notes: ''
  });

  // Statistics
  const [statistics, setStatistics] = useState(null);

  // ========== HELPER FUNCTIONS ==========

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return dateStr.split('T')[0];
  };

  useEffect(() => {
    if (activeTab === 'consolidate') {
      loadAvailablePayrolls();
    } else {
      loadConsolidations();
    }
    loadStatistics();
  }, [activeTab]);

  // ========== LOAD DATA ==========

  const loadAvailablePayrolls = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.department) params.append('department', filters.department);

      const response = await api.get(`/consolidations/available-payrolls?${params}`);
      setAvailablePayrolls(response.data.payrolls || []);
    } catch (error) {
      console.error('Error loading available payrolls:', error);
      notify(t('Error loading payrolls: {error}', { error: error.response?.data?.message || error.message }), 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadConsolidations = async () => {
    try {
      setLoading(true);
      const response = await api.get('/consolidations');
      setConsolidations(response.data.consolidations || []);
    } catch (error) {
      console.error('Error loading consolidations:', error);
      notify(t('Error loading consolidations: {error}', { error: error.response?.data?.message || error.message }), 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await api.get('/consolidations/statistics');
      setStatistics(response.data.statistics);
    } catch (error) {
      console.error('Error loading statistics:', error);
      reportLoadError(error, t('Error loading consolidation statistics'));
    }
  };

  const loadConsolidationDetails = async (id) => {
    try {
      setLoading(true);
      const response = await api.get(`/consolidations/${id}`);
      setSelectedConsolidation(response.data.consolidation);
      setConsolidationItems(response.data.items || []);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Error loading consolidation details:', error);
      notify(t('Error loading details: {error}', { error: error.response?.data?.message || error.message }), 'error');
    } finally {
      setLoading(false);
    }
  };

  // ========== EXPORT FUNCTIONS ==========

  const exportAvailablePayrolls = () => {
    try {
      setExporting(true);
      const data = availablePayrolls.map(p => ({
        [t('Employee')]: `${p.first_name} ${p.last_name}`,
        [t('Department')]: p.department || '',
        [t('Position')]: p.position || '',
        [t('Period Start')]: formatDate(p.period_start),
        [t('Period End')]: formatDate(p.period_end),
        [t('Gross Pay')]: parseFloat(p.gross_pay || 0).toFixed(2),
        [t('Deductions')]: parseFloat(p.total_deductions || 0).toFixed(2),
        [t('Net Pay')]: parseFloat(p.net_pay || 0).toFixed(2),
        [t('Status')]: p.status || ''
      }));

      const success = exportToExcel(data, 'available_payrolls', t('Available Payrolls'));
      if (success) {
        notify(t('Exported successfully!'), 'success');
      } else {
        notify(t('No data to export'), 'error');
      }
    } catch (error) {
      console.error('Export error:', error);
      notify(t('Error exporting: {error}', { error: error.message }), 'error');
    } finally {
      setExporting(false);
    }
  };

  const exportConsolidationsHistory = () => {
    try {
      setExporting(true);
      const data = consolidations.map(c => ({
        [t('Code')]: c.consolidation_code || '',
        [t('Date')]: formatDate(c.consolidation_date),
        [t('Period')]: t('{from} to {to}', { from: formatDate(c.period_start), to: formatDate(c.period_end) }),
        [t('Employees')]: c.employee_count || 0,
        [t('Total Gross')]: parseFloat(c.total_gross || 0).toFixed(2),
        [t('Total Deductions')]: parseFloat(c.total_deductions || 0).toFixed(2),
        [t('Total Net')]: parseFloat(c.total_net || 0).toFixed(2),
        [t('Status')]: c.status || '',
        [t('Payment Date')]: formatDate(c.payment_date) || '-'
      }));

      const success = exportToExcel(data, 'consolidations_history', t('Consolidations'));
      if (success) {
        notify(t('Exported successfully!'), 'success');
      } else {
        notify(t('No data to export'), 'error');
      }
    } catch (error) {
      console.error('Export error:', error);
      notify(t('Error exporting: {error}', { error: error.message }), 'error');
    } finally {
      setExporting(false);
    }
  };

  const exportConsolidationDetails = () => {
    try {
      setExporting(true);
      const data = consolidationItems.map(item => ({
        [t('Consolidation')]: selectedConsolidation.consolidation_code || '',
        [t('Employee')]: `${item.first_name} ${item.last_name}`,
        [t('Department')]: item.department || '',
        [t('Position')]: item.position || '',
        [t('Period')]: t('{from} to {to}', { from: formatDate(item.period_start), to: formatDate(item.period_end) }),
        [t('Gross Pay')]: parseFloat(item.gross_pay || 0).toFixed(2),
        [t('Deductions')]: parseFloat(item.total_deductions || 0).toFixed(2),
        [t('Net Pay')]: parseFloat(item.net_pay || 0).toFixed(2)
      }));

      const success = exportToExcel(
        data, 
        `consolidation_${selectedConsolidation.consolidation_code}_details`,
        t('Details')
      );
      if (success) {
        notify(t('Exported successfully!'), 'success');
      }
    } catch (error) {
      console.error('Export error:', error);
      notify(t('Error exporting: {error}', { error: error.message }), 'error');
    } finally {
      setExporting(false);
    }
  };

  // ========== SELECTION HANDLERS ==========

  const togglePayrollSelection = (payrollId) => {
    const newSelection = new Set(selectedPayrolls);
    if (newSelection.has(payrollId)) {
      newSelection.delete(payrollId);
    } else {
      newSelection.add(payrollId);
    }
    setSelectedPayrolls(newSelection);
  };

  const selectAllPayrolls = () => {
    if (selectedPayrolls.size === availablePayrolls.length) {
      setSelectedPayrolls(new Set());
    } else {
      setSelectedPayrolls(new Set(availablePayrolls.map(p => p.id)));
    }
  };

  // ========== CONSOLIDATION ACTIONS ==========

  const createConsolidation = async () => {
    if (selectedPayrolls.size === 0) {
      notify(t('Please select at least one payroll'), 'warning');
      return;
    }

    if (!await confirmDialog(t('Create consolidation with {count} payroll(s)?', { count: selectedPayrolls.size }))) {
      return;
    }

    try {
      setLoading(true);
      const response = await api.post('/consolidations', {
        payroll_ids: Array.from(selectedPayrolls),
        payment_method: 'bank_transfer',
        notes: t('Consolidation created on {date}', { date: new Date().toLocaleDateString(locale) })
      });

      notify(t('Consolidation created successfully!'), 'success');
      
      // Reset selection
      setSelectedPayrolls(new Set());
      
      // Auto-refresh data
      await loadAvailablePayrolls();
      await loadStatistics();
      
      // Switch to history tab to see the new consolidation
      setActiveTab('history');
    } catch (error) {
      console.error('Error creating consolidation:', error);
      notify(t('Error: {error}', { error: error.response?.data?.message || error.message }), 'error');
    } finally {
      setLoading(false);
    }
  };

  const markAsPaid = async () => {
    if (!selectedConsolidation) return;

    try {
      setLoading(true);
      await api.put(`/consolidations/${selectedConsolidation.id}/mark-paid`, paymentForm);

      notify(t('Consolidation marked as paid!'), 'success');
      setShowPaymentModal(false);
      setShowDetailsModal(false);
      
      // Auto-refresh
      await loadConsolidations();
      await loadStatistics();
      
      setPaymentForm({
        payment_date: toLocalISODate(new Date()),
        payment_reference: '',
        notes: ''
      });
    } catch (error) {
      console.error('Error marking as paid:', error);
      notify(t('Error: {error}', { error: error.response?.data?.message || error.message }), 'error');
    } finally {
      setLoading(false);
    }
  };

  const cancelConsolidation = async (id, code) => {
    const reason = window.prompt(t('Enter reason for cancelling consolidation {code}:', { code }));
    if (!reason) return;

    try {
      setLoading(true);
      await api.delete(`/consolidations/${id}`, {
        data: { reason }
      });

      notify(t('Consolidation cancelled and payrolls reverted!'), 'success');
      setShowDetailsModal(false);
      
      // Auto-refresh
      await loadConsolidations();
      await loadStatistics();
      
      // If on consolidate tab, refresh available payrolls too
      if (activeTab === 'consolidate') {
        await loadAvailablePayrolls();
      }
    } catch (error) {
      console.error('Error cancelling consolidation:', error);
      notify(t('Error: {error}', { error: error.response?.data?.message || error.message }), 'error');
    } finally {
      setLoading(false);
    }
  };

  // ========== CALCULATED VALUES ==========

  const getSelectedTotals = () => {
    const selected = availablePayrolls.filter(p => selectedPayrolls.has(p.id));
    return {
      count: selected.length,
      total_gross: selected.reduce((sum, p) => sum + parseFloat(p.gross_pay || 0), 0),
      total_deductions: selected.reduce((sum, p) => sum + parseFloat(p.total_deductions || 0), 0),
      total_net: selected.reduce((sum, p) => sum + parseFloat(p.net_pay || 0), 0)
    };
  };

  const selectedTotals = getSelectedTotals();

  // ========== RENDER ==========

  return (
    <div className="payroll-consolidation-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('Payroll Consolidation')}</h1>
          <p className="page-sub">{t('Consolidate and process payroll payments')}</p>
        </div>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="kpi-grid">
          <div className="kpi">
            <div className="kpi__label"><span className="kpi__icon"><ClipboardList aria-hidden="true" /></span>{t('Pending Payment')}</div>
            <p className="kpi__value">{statistics.pending_count || 0}</p>
            <div className="kpi__foot">SRD {parseFloat(statistics.pending_amount || 0).toLocaleString(locale)}</div>
          </div>

          <div className="kpi">
            <div className="kpi__label"><span className="kpi__icon"><CircleCheck aria-hidden="true" /></span>{t('Paid')}</div>
            <p className="kpi__value">{statistics.paid_count || 0}</p>
            <div className="kpi__foot">SRD {parseFloat(statistics.paid_amount || 0).toLocaleString(locale)}</div>
          </div>

          <div className="kpi">
            <div className="kpi__label"><span className="kpi__icon"><Coins aria-hidden="true" /></span>{t('Total Consolidations')}</div>
            <p className="kpi__value">{statistics.total_consolidations || 0}</p>
            <div className="kpi__foot">SRD {parseFloat(statistics.total_amount || 0).toLocaleString(locale)}</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="tabbar">
        <button
          className={`tabbar__tab${activeTab === 'consolidate' ? ' is-active' : ''}`}
          onClick={() => setActiveTab('consolidate')}
        >
          {t('Create Consolidation')}
        </button>
        <button
          className={`tabbar__tab${activeTab === 'history' ? ' is-active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          {t('Consolidation History')}
        </button>
      </div>

      {/* CONSOLIDATE TAB */}
      {activeTab === 'consolidate' && (
        <div className="cons-tab">
          {/* Filters */}
          <div className="card cons-filters">
            <h3 className="card__title">{t('Filters')}</h3>
            <div className="cons-filters__grid">
              <div className="cons-field">
                <label className="field-label">{t('Period Start')}</label>
                <input className="input"
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => setFilters({...filters, start_date: e.target.value})}
                />
              </div>
              <div className="cons-field">
                <label className="field-label">{t('Period End')}</label>
                <input className="input"
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => setFilters({...filters, end_date: e.target.value})}
                />
              </div>
              <div className="cons-field">
                <label className="field-label">{t('Department')}</label>
                <input className="input"
                  type="text"
                  value={filters.department}
                  onChange={(e) => setFilters({...filters, department: e.target.value})}
                  placeholder={t('Enter department...')}
                />
              </div>
              <div className="cons-field">
                <button onClick={loadAvailablePayrolls} className="btn btn--primary">
                  {t('Apply Filters')}
                </button>
              </div>
            </div>
          </div>

          {/* Available Payrolls */}
          <div className="cons-section">
            <div className="cons-section__head">
              <h3 className="cons-section__title">{t('Available Payrolls ({count})', { count: availablePayrolls.length })}</h3>
              <div className="page-actions">
                <button 
                  onClick={exportAvailablePayrolls} 
                  className="btn btn--secondary"
                  disabled={availablePayrolls.length === 0 || exporting}
                >
                  {exporting ? t('Exporting...') : <><Download aria-hidden="true" /> {t('Export to Excel')}</>}
                </button>
                <button onClick={selectAllPayrolls} className="btn btn--secondary">
                  {selectedPayrolls.size === availablePayrolls.length ? t('Deselect All') : t('Select All')}
                </button>
                <button 
                  onClick={createConsolidation} 
                  className="btn btn--primary"
                  disabled={selectedPayrolls.size === 0 || loading}
                >
                  {loading ? t('Creating...') : t('Consolidate ({count})', { count: selectedPayrolls.size })}
                </button>
              </div>
            </div>

            {availablePayrolls.length === 0 ? (
              <div className="empty">
                <div className="cons-empty-icon"><Inbox aria-hidden="true" /></div>
                <h4>{t('No Approved Payrolls Available')}</h4>
                <p>{t('All approved payrolls have been consolidated or there are no approved payrolls for the selected period.')}</p>
              </div>
            ) : (
              <div className="cons-table-wrap">
                <table className="data-table cons-table cons-available">
                  <thead>
                    <tr>
                      <th width="50">
                        <input
                          type="checkbox"
                          checked={selectedPayrolls.size === availablePayrolls.length && availablePayrolls.length > 0}
                          onChange={selectAllPayrolls}
                        />
                      </th>
                      <th>{t('Employee')}</th>
                      <th>{t('Department')}</th>
                      <th>{t('Period')}</th>
                      <th className="num">{t('Gross Pay')}</th>
                      <th className="num">{t('Deductions')}</th>
                      <th className="num">{t('Net Pay')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {availablePayrolls.map(payroll => (
                      <tr 
                        key={payroll.id}
                        className={selectedPayrolls.has(payroll.id) ? 'is-selected' : ''}
                        onClick={() => togglePayrollSelection(payroll.id)}
                      >
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedPayrolls.has(payroll.id)}
                            onChange={() => togglePayrollSelection(payroll.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td>
                          <strong>{payroll.first_name} {payroll.last_name}</strong>
                          <div className="cons-muted">{payroll.position}</div>
                        </td>
                        <td data-label={t('Department')}>{payroll.department}</td>
                        <td data-label={t('Period')}>
                          {t('{from} to {to}', { from: formatDate(payroll.period_start), to: formatDate(payroll.period_end) })}
                        </td>
                        <td className="num" data-label={t('Gross Pay')}>SRD {parseFloat(payroll.gross_pay).toLocaleString(locale)}</td>
                        <td className="num cons-minus" data-label={t('Deductions')}>-SRD {parseFloat(payroll.total_deductions).toLocaleString(locale)}</td>
                        <td className="num cons-net" data-label={t('Net Pay')}>SRD {parseFloat(payroll.net_pay).toLocaleString(locale)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Selected Summary */}
          {selectedPayrolls.size > 0 && (
            <div className="cons-summary">
              <h4>{t('Selected Summary')}</h4>
              <div className="cons-summary__grid">
                <div className="cons-summary__item">
                  <span className="cons-summary__label">{t('Payrolls:')}</span>
                  <span className="cons-summary__value">{selectedTotals.count}</span>
                </div>
                <div className="cons-summary__item">
                  <span className="cons-summary__label">{t('Total Gross:')}</span>
                  <span className="cons-summary__value">SRD {selectedTotals.total_gross.toLocaleString(locale)}</span>
                </div>
                <div className="cons-summary__item">
                  <span className="cons-summary__label">{t('Total Deductions:')}</span>
                  <span className="cons-summary__value cons-minus">-SRD {selectedTotals.total_deductions.toLocaleString(locale)}</span>
                </div>
                <div className="cons-summary__item is-net">
                  <span className="cons-summary__label">{t('Total Net Pay:')}</span>
                  <span className="cons-summary__value">SRD {selectedTotals.total_net.toLocaleString(locale)}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* HISTORY TAB */}
      {activeTab === 'history' && (
        <div className="cons-tab">
          <div className="cons-section__head">
            <h3 className="cons-section__title">{t('Consolidation History ({count})', { count: consolidations.length })}</h3>
            <button 
              onClick={exportConsolidationsHistory} 
              className="btn btn--secondary"
              disabled={consolidations.length === 0 || exporting}
            >
              {exporting ? t('Exporting...') : <><Download aria-hidden="true" /> {t('Export to Excel')}</>}
            </button>
          </div>

          {consolidations.length === 0 ? (
            <div className="empty">
              <div className="cons-empty-icon"><Inbox aria-hidden="true" /></div>
              <h4>{t('No Consolidations Yet')}</h4>
              <p>{t('Create your first consolidation from the "Create Consolidation" tab.')}</p>
            </div>
          ) : (
            <div className="cons-table-wrap">
              <table className="data-table cons-table">
                <thead>
                  <tr>
                    <th>{t('Code')}</th>
                    <th>{t('Date')}</th>
                    <th>{t('Period')}</th>
                    <th>{t('Employees')}</th>
                    <th className="num">{t('Total Amount')}</th>
                    <th>{t('Status')}</th>
                    <th>{t('Actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {consolidations.map(cons => (
                    <tr key={cons.id}>
                      <td>
                        <strong>{cons.consolidation_code}</strong>
                      </td>
                      <td>{formatDate(cons.consolidation_date)}</td>
                      <td>
                        {t('{from} to {to}', { from: formatDate(cons.period_start), to: formatDate(cons.period_end) })}
                      </td>
                      <td>{cons.employee_count}</td>
                      <td className="num">SRD {parseFloat(cons.total_net).toLocaleString(locale)}</td>
                      <td>
                        <StatusBadge status={cons.status}>
                          {cons.status === 'pending_payment' && t('Pending')}
                          {cons.status === 'paid' && t('Paid')}
                          {cons.status === 'cancelled' && t('Cancelled')}
                        </StatusBadge>
                      </td>
                      <td>
                        <div className="cons-actions">
                          <button
                            onClick={() => loadConsolidationDetails(cons.id)}
                            type="button"
                            className="btn btn--secondary btn--icon"
                            title={t('View Details')}
                            aria-label={t('View Details')}
                          >
                            <Eye aria-hidden="true" />
                          </button>
                          {cons.status === 'pending_payment' && (
                            <>
                              <button
                                onClick={() => {
                                  setSelectedConsolidation(cons);
                                  setShowPaymentModal(true);
                                }}
                                type="button"
                                className="btn btn--secondary btn--icon"
                                title={t('Mark as Paid')}
                                aria-label={t('Mark as Paid')}
                              >
                                <Banknote aria-hidden="true" />
                              </button>
                              <button
                                onClick={() => cancelConsolidation(cons.id, cons.consolidation_code)}
                                type="button"
                                className="btn btn--secondary btn--icon btn--danger-text"
                                title={t('Cancel')}
                                aria-label={t('Cancel')}
                              >
                                <X aria-hidden="true" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* DETAILS MODAL */}
      {showDetailsModal && selectedConsolidation && (
        <Modal open title={t('Consolidation Details')} onClose={() => setShowDetailsModal(false)} size="lg">
            <div className="cons-dialog">
              <div className="cons-dialog__tools">
                <button
                  type="button"
                  onClick={exportConsolidationDetails}
                  className="btn btn--secondary btn--icon"
                  disabled={exporting}
                  title={t('Export Details')}
                  aria-label={t('Export Details')}
                >
                  {exporting ? <Hourglass aria-hidden="true" /> : <Download aria-hidden="true" />}
                </button>
              </div>
              {/* Consolidation Info */}
              <div className="cons-info">
                <div className="cons-info__item">
                  <label className="field-label">{t('Code:')}</label>
                  <strong>{selectedConsolidation.consolidation_code}</strong>
                </div>
                <div className="cons-info__item">
                  <label className="field-label">{t('Date:')}</label>
                  <span>{formatDate(selectedConsolidation.consolidation_date)}</span>
                </div>
                <div className="cons-info__item">
                  <label className="field-label">{t('Period:')}</label>
                  <span>{t('{from} to {to}', { from: formatDate(selectedConsolidation.period_start), to: formatDate(selectedConsolidation.period_end) })}</span>
                </div>
                <div className="cons-info__item">
                  <label className="field-label">{t('Status:')}</label>
                  <StatusBadge status={selectedConsolidation.status}>
                    {t(selectedConsolidation.status.replace('_', ' '))}
                  </StatusBadge>
                </div>
                <div className="cons-info__item">
                  <label className="field-label">{t('Total Gross:')}</label>
                  <span>SRD {parseFloat(selectedConsolidation.total_gross).toLocaleString(locale)}</span>
                </div>
                <div className="cons-info__item">
                  <label className="field-label">{t('Total Deductions:')}</label>
                  <span>SRD {parseFloat(selectedConsolidation.total_deductions).toLocaleString(locale)}</span>
                </div>
                <div className="cons-info__item is-net">
                  <label className="field-label">{t('Total Net:')}</label>
                  <strong>SRD {parseFloat(selectedConsolidation.total_net).toLocaleString(locale)}</strong>
                </div>
                <div className="cons-info__item">
                  <label className="field-label">{t('Employees:')}</label>
                  <span>{selectedConsolidation.employee_count}</span>
                </div>
              </div>

              {/* Items Table */}
              <h4 className="cons-subtitle">{t('Included Payrolls')}</h4>
              <div className="cons-table-wrap">
                <table className="data-table cons-table">
                  <thead>
                    <tr>
                      <th>{t('Employee')}</th>
                      <th>{t('Department')}</th>
                      <th>{t('Period')}</th>
                      <th className="num">{t('Gross')}</th>
                      <th className="num">{t('Deductions')}</th>
                      <th className="num">{t('Net')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {consolidationItems.map(item => (
                      <tr key={item.id}>
                        <td>{item.first_name} {item.last_name}</td>
                        <td>{item.department}</td>
                        <td>{t('{from} to {to}', { from: formatDate(item.period_start), to: formatDate(item.period_end) })}</td>
                        <td className="num">SRD {parseFloat(item.gross_pay).toLocaleString(locale)}</td>
                        <td className="num">SRD {parseFloat(item.total_deductions).toLocaleString(locale)}</td>
                        <td className="num">SRD {parseFloat(item.net_pay).toLocaleString(locale)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="cons-form-actions">
              <button onClick={() => setShowDetailsModal(false)} className="btn btn--secondary">
                {t('Close')}
              </button>
            </div>
        </Modal>
      )}

      {/* PAYMENT MODAL */}
      {showPaymentModal && selectedConsolidation && (
        <Modal open title={t('Mark as Paid')} onClose={() => setShowPaymentModal(false)} size="md">

            <div className="cons-dialog">
              <p><strong>{t('Consolidation:')}</strong> {selectedConsolidation.consolidation_code}</p>
              <p><strong>{t('Amount:')}</strong> SRD {parseFloat(selectedConsolidation.total_net).toLocaleString(locale)}</p>

              <div className="cons-field">
                <label className="field-label">{t('Payment Date *')}</label>
                <input className="input"
                  type="date"
                  value={paymentForm.payment_date}
                  onChange={(e) => setPaymentForm({...paymentForm, payment_date: e.target.value})}
                  required
                />
              </div>

              <div className="cons-field">
                <label className="field-label">{t('Payment Reference')}</label>
                <input className="input"
                  type="text"
                  value={paymentForm.payment_reference}
                  onChange={(e) => setPaymentForm({...paymentForm, payment_reference: e.target.value})}
                  placeholder={t('e.g., Bank transfer #12345')}
                />
              </div>

              <div className="cons-field">
                <label className="field-label">{t('Notes')}</label>
                <textarea className="textarea"
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                  rows="3"
                  placeholder={t('Optional notes...')}
                />
              </div>
            </div>

            <div className="cons-form-actions">
              <button onClick={() => setShowPaymentModal(false)} className="btn btn--secondary">
                {t('Cancel')}
              </button>
              <button onClick={markAsPaid} className="btn btn--primary" disabled={loading}>
                {loading ? t('Processing...') : t('Confirm Payment')}
              </button>
            </div>
        </Modal>
      )}
    </div>
  );
};

export default PayrollConsolidation;